
import { useState, useEffect } from "react";
import { Plus, Edit, Trash2, Folder, Tag, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../api";

interface CategoryWithTags {
  _id: string;
  name: string;
  tags: string[];
  _creationTime: number;
}

const LIGHT_BLUE_COLOR = "#3B82F6";

export const CategoriesManagement = () => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryWithTags | null>(null);
  const [editingTag, setEditingTag] = useState<{ id: string; name: string; categoryId: string } | null>(null);
  const [selectedCategoryForTag, setSelectedCategoryForTag] = useState<string>("");
  const [categoryName, setCategoryName] = useState("");
  const [tagName, setTagName] = useState("");
  const { toast } = useToast();

  // Convex queries and mutations
  const categoriesData = useQuery(api.appCategories.list.categories);
  const addCategory = useMutation(api.appCategories.add.category);
  const updateCategory = useMutation(api.appCategories.update.category);
  const deleteCategory = useMutation(api.appCategories.delete.category);

  console.log("Categories data from Convex:", categoriesData);

  // Handle the case where categoriesData might be undefined or in an unexpected format
  const categoriesWithTags: CategoryWithTags[] = categoriesData ? 
    (Array.isArray(categoriesData) ? categoriesData : []).map((category: any) => ({
      _id: category._id || category.id || "",
      name: category.name || "",
      tags: Array.isArray(category.tags) ? category.tags : [],
      _creationTime: category._creationTime || Date.now(),
    })) : [];

  const toggleCategoryExpansion = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleCreateOrUpdateCategory = async () => {
    if (!categoryName.trim()) return;

    try {
      if (editingCategory) {
        await updateCategory({
          name: categoryName.trim(),
          tags: editingCategory.tags,
        });
        
        toast({
          title: "Success",
          description: "Category updated successfully",
        });
      } else {
        await addCategory({
          name: categoryName.trim(),
          tags: [],
        });
        
        toast({
          title: "Success",
          description: "Category created successfully",
        });
      }

      setCategoryName("");
      setEditingCategory(null);
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error saving category:', error);
      toast({
        title: "Error",
        description: "Failed to save category",
        variant: "destructive",
      });
    }
  };

  const handleCreateOrUpdateTag = async () => {
    if (!tagName.trim() || !selectedCategoryForTag) return;

    try {
      const category = categoriesWithTags.find(cat => cat._id === selectedCategoryForTag);
      if (!category) {
        console.error('Category not found for tag operation');
        return;
      }

      let updatedTags: string[];
      
      if (editingTag) {
        // Update existing tag
        updatedTags = category.tags.map(tag => 
          tag === editingTag.name ? tagName.trim() : tag
        );
      } else {
        // Add new tag
        updatedTags = [...category.tags, tagName.trim()];
      }

      await updateCategory({
        name: category.name,
        tags: updatedTags,
      });
      
      toast({
        title: "Success",
        description: editingTag ? "Tag updated successfully" : "Tag created successfully",
      });

      setTagName("");
      setSelectedCategoryForTag("");
      setEditingTag(null);
      setIsTagDialogOpen(false);
    } catch (error) {
      console.error('Error saving tag:', error);
      toast({
        title: "Error",
        description: "Failed to save tag",
        variant: "destructive",
      });
    }
  };

  const handleEditCategory = (category: CategoryWithTags) => {
    setEditingCategory(category);
    setCategoryName(category.name);
    setIsDialogOpen(true);
  };

  const handleEditTag = (tag: string, categoryId: string) => {
    setEditingTag({ id: tag, name: tag, categoryId });
    setTagName(tag);
    setSelectedCategoryForTag(categoryId);
    setIsTagDialogOpen(true);
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!confirm("Are you sure you want to delete this category? This will also delete all associated tags.")) return;

    try {
      await deleteCategory({
        category: categoryId as any,
      });
      
      toast({
        title: "Success",
        description: "Category deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: "Error",
        description: "Failed to delete category",
        variant: "destructive",
      });
    }
  };

  const handleDeleteTag = async (tagName: string, categoryId: string) => {
    if (!confirm("Are you sure you want to delete this tag?")) return;

    try {
      const category = categoriesWithTags.find(cat => cat._id === categoryId);
      if (!category) {
        console.error('Category not found for tag deletion');
        return;
      }

      const updatedTags = category.tags.filter(tag => tag !== tagName);

      await updateCategory({
        name: category.name,
        tags: updatedTags,
      });
      
      toast({
        title: "Success",
        description: "Tag deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast({
        title: "Error",
        description: "Failed to delete tag",
        variant: "destructive",
      });
    }
  };

  const openCreateCategoryDialog = () => {
    setEditingCategory(null);
    setCategoryName("");
    setIsDialogOpen(true);
  };

  const openCreateTagDialog = (categoryId?: string) => {
    setEditingTag(null);
    setTagName("");
    setSelectedCategoryForTag(categoryId || "");
    setIsTagDialogOpen(true);
  };

  const isLoading = categoriesData === undefined;

  if (isLoading) {
    return (
      <div className="border rounded-lg p-8 text-center text-gray-500">
        Loading categories and tags...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Categories & Tags</h1>
        <p className="text-gray-600">Manage app categories and tags to organize your application catalog</p>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Create and manage categories and their associated tags to organize your apps effectively.
        </p>
        <div className="flex gap-2">
          <Dialog open={isTagDialogOpen} onOpenChange={setIsTagDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingTag ? "Edit Tag" : "Create Tag"}
                </DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="category">Category</Label>
                  <select
                    id="category"
                    value={selectedCategoryForTag}
                    onChange={(e) => setSelectedCategoryForTag(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Select a category</option>
                    {categoriesWithTags.map((category) => (
                      <option key={category._id} value={category._id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="tagName">Tag Name</Label>
                  <Input
                    id="tagName"
                    placeholder="Enter tag name"
                    value={tagName}
                    onChange={(e) => setTagName(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Preview</Label>
                  <div className="flex items-center">
                    <Badge style={{ backgroundColor: LIGHT_BLUE_COLOR, color: 'white' }}>
                      {tagName || "Preview"}
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsTagDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateOrUpdateTag}
                  disabled={!tagName.trim() || !selectedCategoryForTag}
                >
                  {editingTag ? "Update" : "Create"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreateCategoryDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Add Category
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingCategory ? "Edit Category" : "Create Category"}
                </DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    placeholder="Enter category name"
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateOrUpdateCategory}
                  disabled={!categoryName.trim()}
                >
                  {editingCategory ? "Update" : "Create"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="border rounded-lg">
        {categoriesWithTags.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Folder className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p>No categories found. Create your first category to get started.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]"></TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead className="w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categoriesWithTags.map((category) => (
                <>
                  <TableRow key={category._id}>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-0 h-auto"
                        onClick={() => toggleCategoryExpansion(category._id)}
                      >
                        {expandedCategories.has(category._id) ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Folder className="w-4 h-4 text-gray-600" />
                        <span className="font-medium">{category.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {category.tags.length} tag{category.tags.length !== 1 ? 's' : ''}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openCreateTagDialog(category._id)}
                        >
                          <Tag className="w-3 h-3 mr-1" />
                          Add Tag
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditCategory(category)}
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteCategory(category._id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  
                  {expandedCategories.has(category._id) && category.tags.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="bg-gray-50 p-0">
                        <div className="p-4">
                          <div className="flex flex-wrap gap-2">
                            {category.tags.map((tag) => (
                              <div key={tag} className="flex items-center gap-2 p-2 bg-white rounded border">
                                <Badge style={{ backgroundColor: LIGHT_BLUE_COLOR, color: 'white' }}>
                                  {tag}
                                </Badge>
                                <div className="flex items-center gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEditTag(tag, category._id)}
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDeleteTag(tag, category._id)}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  
                  {expandedCategories.has(category._id) && category.tags.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="bg-gray-50">
                        <div className="p-4 text-center text-gray-500">
                          <Tag className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm">No tags in this category</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};
